import type { LintRule } from './types';

/**
 * Core determinism rules for HTML compositions. The render pipeline rasterizes one frame at a
 * time by seeking the composition's timeline to a progress value, so a composition must be a
 * deterministic, seekable pure-function-of-time. These rules reject the constructs that break
 * that contract. They are intentionally a small, extensible starter set — more rules (font
 * inlining, node budget, video handling) can be added as the feature matures.
 */

/**
 * Extract just the CSS text from a composition (the contents of `<style>` blocks and `style="..."`
 * attributes). The animation/transition checks run against this rather than the whole HTML so they
 * don't false-positive on JS object properties of the same name (e.g. a GSAP tween's
 * `{ transition: ... }`), which appear in `<script>` blocks. New regexes are created per call so
 * their `lastIndex` never leaks between invocations.
 */
function extractCssText(html: string): string {
    const parts: string[] = [];
    const styleBlock = /<style\b[^>]*>([\s\S]*?)<\/style>/gi;
    const styleAttr = /style\s*=\s*"([^"]*)"|style\s*=\s*'([^']*)'/gi;
    let match: RegExpExecArray | null;
    while ((match = styleBlock.exec(html)) !== null) {
        parts.push(match[1]);
    }
    while ((match = styleAttr.exec(html)) !== null) {
        parts.push(match[1] ?? match[2] ?? '');
    }
    return parts.join('\n');
}

/**
 * CSS animations/transitions run on wall-clock time and cannot be seeked: when the composition is
 * rasterized off-screen they freeze at their starting frame. All motion must come from the
 * seekable timeline instead.
 */
const noCssAnimation: LintRule = {
    id: 'no-css-animation',
    severity: 'error',
    guidance:
        'Animate ONLY through the GSAP timeline. Never use CSS `@keyframes`, `animation`, or `transition` — they run on wall-clock time and freeze at their first frame when the composition is rasterized off-screen.',
    check(html: string): string[] {
        const messages: string[] = [];
        const css = extractCssText(html);
        if (/@(-webkit-)?keyframes\b/i.test(css)) {
            messages.push(
                'CSS @keyframes are not seekable and freeze when the composition is rasterized. Animate via the timeline instead.',
            );
        }
        if (/(?:^|[{;\s])animation(?:-name|-duration)?\s*:/i.test(css)) {
            messages.push(
                'CSS `animation` declarations are not seekable. Animate via the timeline instead.',
            );
        }
        if (/(?:^|[{;\s])transition\s*:/i.test(css)) {
            messages.push(
                'CSS `transition` is driven by wall-clock time and is not seekable. Animate via the timeline instead.',
            );
        }
        return messages;
    },
};

/**
 * An infinitely-repeating timeline has no finite duration, so it cannot be mapped to a normalized
 * progress value. Ambient loops must be expressed as a finite timeline the host repeats.
 */
const noInfiniteTimeline: LintRule = {
    id: 'no-infinite-timeline',
    severity: 'error',
    guidance:
        'Keep the timeline finite — never `repeat: -1` or `Infinity`. The host maps a normalized 0→1 progress across the scene, which requires a finite duration; express ambient loops as a finite timeline.',
    check(html: string): string[] {
        if (/repeat\s*:\s*(-1|Infinity)/.test(html)) {
            return [
                'An infinitely-repeating timeline (`repeat: -1`) has no finite duration and cannot be seeked. Use a finite timeline.',
            ];
        }
        return [];
    },
};

/**
 * Wall-clock and random sources make a composition non-deterministic, so the same progress value
 * would rasterize to different frames across renders.
 */
const noNonDeterministicSource: LintRule = {
    id: 'no-nondeterministic-source',
    severity: 'error',
    guidance:
        'Never call `Date.now()`, `new Date()`, `Math.random()`, or `performance.now()`. Each frame must be a pure function of timeline progress (frames may render out of order or in a fresh context); derive any variation from the timeline or a seeded value.',
    check(html: string): string[] {
        const messages: string[] = [];
        if (/\bDate\.now\s*\(|\bnew\s+Date\s*\(\s*\)/.test(html)) {
            messages.push(
                '`Date.now()` / `new Date()` make the composition non-deterministic. Drive all timing from the timeline.',
            );
        }
        if (/\bMath\.random\s*\(/.test(html)) {
            messages.push(
                '`Math.random()` makes the composition non-deterministic. Use a seeded value instead.',
            );
        }
        if (/\bperformance\.now\s*\(/.test(html)) {
            messages.push(
                '`performance.now()` makes the composition non-deterministic. Drive all timing from the timeline.',
            );
        }
        return messages;
    },
};

/**
 * The host seeks `window.__timelines.main` to drive the composition. Without it the composition
 * can still be rasterized, but only as a static frame, so this is a warning rather than an error.
 */
const requiresTimeline: LintRule = {
    id: 'requires-timeline',
    severity: 'warning',
    guidance:
        'Register a single PAUSED, seekable GSAP timeline at `window.__timelines.main`. The host drives the composition by seeking this exact global — it never plays it — so without it the composition renders as a single static frame.',
    check(html: string): string[] {
        if (!/__timelines/.test(html)) {
            return [
                'No `window.__timelines.main` timeline found. The composition will render as a static frame.',
            ];
        }
        return [];
    },
};

export const CORE_LINT_RULES: LintRule[] = [
    noCssAnimation,
    noInfiniteTimeline,
    noNonDeterministicSource,
    requiresTimeline,
];
